import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { MOTIVO_PAUSA_MAX, MOTIVO_PAUSA_MIN, motivoDaPausa } from '@malgoos/shared';
import { Botao } from '../../componentes/base';
import { AreaSensivel, Alternador, CampoData, EntradaSensivel, Rotulo, Selecao } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro, post } from '../../nucleo/api';
import { dinheiro, hojeIso, nomePessoa, textoLivre } from '../../nucleo/formato';
import { useListas } from './crm-listas';
import { rotuloMotivoPerda } from '../../nucleo/rotulos';
import { useSessao } from '../../nucleo/sessao';
import type { Motivo, Oportunidade } from './tipos';
import { anunciarOcultos } from '../../componentes/sensivel';

/**
 * M2.5 MarcarGanha: confirmação com a opção de converter em projeto (interface cadastros.projetos).
 * Ajustes do CRM (27/10, F12): com `completo` (a ficha no novo desenho), a data de fechamento, o valor total e a anotação do
 * RD e, depois, o "Parabéns pela venda" com as vendas do mês do responsável (desligável nas Configurações do CRM).
 */
export function ModalGanhar({ oportunidade, aoFechar, aoConcluir, completo }: { oportunidade: Oportunidade; aoFechar: () => void; aoConcluir: () => void; completo?: boolean }) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const listas = useListas();
  const podeConverter = s.instalado('cadastros') && s.pode('cadastros.criar');
  const [converter, setConverter] = useState(false);
  const [nome, setNome] = useState(oportunidade.titulo);
  const [dataFechamento, setDataFechamento] = useState(hojeIso());
  const [anotacao, setAnotacao] = useState('');
  const [celebracao, setCelebracao] = useState<{ responsavel_nome: string | null; vendas: number; total_centavos: number; moeda: string } | null>(null);
  const ganhar = useMutation({
    mutationFn: () =>
      post<{ projeto_id: string | null }>(`/pipeline/oportunidades/${oportunidade.id}/ganhar`, {
        converter_em_projeto: converter,
        ...(converter && nome.trim() ? { projeto_nome: nome.trim() } : {}),
        ...(completo ? { data_fechamento: dataFechamento || undefined, ...(anotacao.trim() ? { observacao: anotacao.trim() } : {}) } : {}),
      }),
    onSuccess: async () => {
      if (completo && listas.data?.celebrar_venda !== false) {
        const c = await get<{ responsavel_nome: string | null; vendas: number; total_centavos: number; moeda: string }>(`/pipeline/crm/negociacoes/${oportunidade.id}/celebracao`).catch(() => null);
        if (c) return setCelebracao(c);
      }
      toast.sucesso(tn('comum.salvo'));
      aoConcluir();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  if (celebracao)
    return (
      <Modal
        aberto
        aoFechar={aoConcluir}
        titulo={t('crm.ficha2.parabens')}
        rodape={
          <Botao variante="primario" onClick={aoConcluir} data-celebracao-continuar="">
            {t('crm.ficha2.continuar')}
          </Botao>
        }
      >
        <div className="flex flex-col gap-2 text-sm" data-celebracao="">
          <p>{t('crm.ficha2.vendaMarcada', { titulo: textoLivre(oportunidade.titulo, oportunidade.parceiro_nome) })}</p>
          <p>
            {t('crm.ficha2.vendasMes', { count: celebracao.vendas, nome: celebracao.responsavel_nome ? nomePessoa(celebracao.responsavel_nome) : t('crm.ficha2.semResponsavel'), total: anunciarOcultos(dinheiro(celebracao.total_centavos, celebracao.moeda)) })}
          </p>
        </div>
      </Modal>
    );
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('modal.ganharTitulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={ganhar.isPending} onClick={() => ganhar.mutate()}>
            {t('acoes.confirmarGanho')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-tinta-2">
          <span className="font-medium text-tinta-1">{textoLivre(oportunidade.titulo, oportunidade.parceiro_nome)}</span> ({anunciarOcultos(dinheiro(oportunidade.valor_centavos, oportunidade.moeda))}). {t('modal.ganharTexto')}
        </p>
        {completo && (
          <>
            <Rotulo id="ganho-data" rotulo={t('crm.ficha.campos.fechamento')}>
              <CampoData id="ganho-data" type="date" max={hojeIso()} value={dataFechamento} onChange={(e) => setDataFechamento(e.target.value)} />
            </Rotulo>
            <Rotulo id="ganho-valor" rotulo={t('crm.ficha.campos.valor')}>
              <EntradaSensivel id="ganho-valor" mascara={null} disabled value={dinheiro(oportunidade.valor_centavos, oportunidade.moeda)} onChange={() => undefined} />
            </Rotulo>
            <Rotulo id="ganho-anotacao" rotulo={t('crm.ficha2.anotacao')}>
              <AreaSensivel
                nomesNoTexto={[oportunidade.parceiro_nome]}
                id="ganho-anotacao"
                rows={3}
                maxLength={2000}
                value={anotacao}
                onChange={(e) => setAnotacao(e.target.value)}
                className="w-full min-w-0 rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 placeholder:text-tinta-3 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco"
              />
            </Rotulo>
          </>
        )}
        {podeConverter && (
          <div className="flex items-start justify-between gap-4 rounded-lg border border-borda bg-sup-2 p-3">
            <div className="min-w-0">
              <p className="text-sm font-medium">{t('campos.converter')}</p>
              <p className="text-xs text-tinta-3">{t('modal.converterTexto')}</p>
            </div>
            <Alternador marcado={converter} aoMudar={setConverter} rotulo={t('campos.converter')} />
          </div>
        )}
        {podeConverter && converter && (
          <Rotulo id="projeto-nome" rotulo={t('campos.projetoNome')}>
            {/* r11 visual M1: o nome vem do título, que cita o cliente; sem foco segue a regra do texto livre. */}
            <EntradaSensivel mascara={null} livre nomesNoTexto={[oportunidade.parceiro_nome]} id="projeto-nome" value={nome} maxLength={200} onChange={(e) => setNome(e.target.value)} />
          </Rotulo>
        )}
      </div>
    </Modal>
  );
}

/** M2.5 MarcarPerdida: o motivo é obrigatório. */
export function ModalPerder({ oportunidade, aoFechar, aoConcluir }: { oportunidade: Oportunidade; aoFechar: () => void; aoConcluir: () => void }) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const [motivo, setMotivo] = useState('');
  const [obs, setObs] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const motivos = useQuery({ queryKey: ['pipeline', 'motivos'], queryFn: () => get<{ itens: Motivo[] }>('/pipeline/motivos-perda') });
  const perder = useMutation({
    mutationFn: () => post(`/pipeline/oportunidades/${oportunidade.id}/perder`, { motivo_perda_id: motivo, ...(obs.trim() ? { observacao: obs.trim() } : {}) }),
    onSuccess: () => {
      toast.sucesso(tn('comum.salvo'));
      aoConcluir();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const itens = motivos.data?.itens ?? [];
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('modal.perderTitulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao
            variante="perigo"
            carregando={perder.isPending}
            onClick={() => {
              if (!motivo) return setErro(tn('erros.obrigatorio'));
              setErro(null);
              perder.mutate();
            }}
          >
            {t('acoes.confirmarPerda')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-tinta-2">
          <span className="font-medium text-tinta-1">{textoLivre(oportunidade.titulo, oportunidade.parceiro_nome)}</span>. {t('modal.perderTexto')}
        </p>
        <Rotulo id="motivo-perda" rotulo={t('campos.motivo')} obrigatorio erro={erro ?? undefined} ajuda={!motivos.isPending && itens.length === 0 ? t('modal.semMotivos') : undefined}>
          <Selecao id="motivo-perda" aria-invalid={!!erro} opcoes={itens.map((m) => ({ valor: m.id, rotulo: rotuloMotivoPerda(m.nome) }))} vazio={tn('comum.selecione')} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </Rotulo>
        <Rotulo id="motivo-obs" rotulo={t('campos.observacao')}>
          <AreaSensivel
            nomesNoTexto={[oportunidade.parceiro_nome]}
            id="motivo-obs"
            rows={3}
            maxLength={2000}
            value={obs}
            onChange={(e) => setObs(e.target.value)}
            className="w-full min-w-0 rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 placeholder:text-tinta-3 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco"
          />
        </Rotulo>
      </div>
    </Modal>
  );
}

/**
 * Lista de 30/10 (B), item 9: pausar e retomar a negociação pedem a justificativa (o texto vai ao histórico). A regra é a
 * `motivoDaPausa` do `packages/shared`, a mesma da API, e o botão só manda o texto normalizado.
 */
export function ModalPausa({ oportunidade, retomar, aoFechar, aoConcluir }: { oportunidade: Oportunidade; retomar: boolean; aoFechar: () => void; aoConcluir: () => void }) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const enviar = useMutation({
    mutationFn: (texto: string) => post(`/pipeline/oportunidades/${oportunidade.id}/${retomar ? 'retomar' : 'pausar'}`, { motivo: texto }),
    onSuccess: () => {
      toast.sucesso(tn('comum.salvo'));
      aoConcluir();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const confirmar = () => {
    const r = motivoDaPausa(motivo);
    if (!r.ok) return setErro(t('crm.pausa.erroMotivo', { min: MOTIVO_PAUSA_MIN, max: MOTIVO_PAUSA_MAX }));
    setErro(null);
    enviar.mutate(r.valor);
  };
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t(retomar ? 'crm.pausa.tituloRetomar' : 'crm.pausa.tituloPausar')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={enviar.isPending} onClick={confirmar} data-confirmar-pausa="">
            {t(retomar ? 'crm.pausa.confirmarRetomar' : 'crm.pausa.confirmarPausar')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4" data-modal-pausa={retomar ? 'retomar' : 'pausar'}>
        <p className="text-sm text-tinta-2">
          <span className="font-medium text-tinta-1">{textoLivre(oportunidade.titulo, oportunidade.parceiro_nome)}</span>. {t(retomar ? 'crm.pausa.textoRetomar' : 'crm.pausa.textoPausar')}
        </p>
        <Rotulo id="motivo-pausa" rotulo={t('crm.pausa.motivo')} obrigatorio erro={erro ?? undefined}>
          <AreaSensivel
            nomesNoTexto={[oportunidade.parceiro_nome]}
            id="motivo-pausa"
            rows={3}
            maxLength={MOTIVO_PAUSA_MAX}
            aria-invalid={!!erro}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            className="w-full min-w-0 rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 placeholder:text-tinta-3 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco"
          />
        </Rotulo>
      </div>
    </Modal>
  );
}
